import React, { Fragment, useState, useContext, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Scrollbar from 'react-smooth-scrollbar';
import { v4 as uuid } from 'uuid';
import { Card, CardBody } from 'reactstrap';
import GraphQL from '../../../GraphQL';
import {
  accountValues,
  receivablesByAccount,
  receivablesByCategory,
} from '../../../shared/queries/Dashboard';
import FinancialCards from './FinancialCards';
import ReceivablesByAccountChart from './ReceivablesByAccountChart';
import ReceivablesByCategoryChart from './ReceivablesByCategoryChart';
import SmartContext from '../../App/context/SmartContext';
import Panel from '../../../shared/components/Panel';
import colors from '../../../shared/utils/colors';
import { formatCurrency } from '../../../shared/utils/smartutils';

const FinancialBoard = (props) => {
  const { t } = props;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutoutPercentage: 90,

    plugins: {
      datalabels: false,
    },

    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          return ` ${data.labels[tooltipItem.index]}: ${formatCurrency(
            data.datasets[0].data[tooltipItem.index]
          )}`;
        },
      },
    },
  };

  const [financialData, setFinancialData] = useState({});
  const [accountsLabels, setAccountsLabels] = useState([]);
  const [accountsValues, setAccountsValues] = useState([]);
  const [categorysLabels, setCategorysLabels] = useState([]);
  const [categorysValues, setCategorysValues] = useState([]);
  const context = useContext(SmartContext);
  const { listOfPermission } = context;

  const loadFinancialData = () => {
    if (listOfPermission.length) {
      if (
        listOfPermission.some(
          (item) => item.name === t('dashboard.financialBoard.title')
        )
      ) {
        const query = accountValues();
        GraphQL(query, {})
          .then((res) => {
            if (res.accountValues) {
              setFinancialData(res.accountValues);
            }
          })
          .catch(() => {});
      }
    }
  };

  const loadReceivedByAccount = () => {
    if (listOfPermission.length) {
      if (
        listOfPermission.some(
          (item) => item.name === t('dashboard.financialBoard.title')
        )
      ) {
        const query = receivablesByAccount();
        GraphQL(query, {})
          .then((res) => {
            if (res.receivablesByAccount.length) {
              const mappedLabels = res.receivablesByAccount.map(
                (item) => item.label
              );
              setAccountsLabels(mappedLabels);
              const mappedValues = res.receivablesByAccount.map(
                (item) => item.value
              );
              setAccountsValues(mappedValues);
            }
          })
          .catch(() => {});
      }
    }
  };

  const loadReceivedByCategory = () => {
    if (listOfPermission.length) {
      if (
        listOfPermission.some(
          (item) => item.name === t('dashboard.financialBoard.title')
        )
      ) {
        const query = receivablesByCategory();
        GraphQL(query, {})
          .then((res) => {
            if (res.receivablesByCategory) {
              const mappedLabels = res.receivablesByCategory.map(
                (item) => item.label
              );
              setCategorysLabels(mappedLabels);

              const mappedValues = res.receivablesByCategory.map(
                (item) => item.value
              );

              setCategorysValues(mappedValues);
            }
          })
          .catch(() => {});
      }
    }
  };

  useEffect(() => {
    loadFinancialData();
    loadReceivedByAccount();
    loadReceivedByCategory();
  }, []);

  return (
    <Fragment>
      <h2 className="dashboard-titles">
        {t('dashboard.financialBoard.title')}
      </h2>
      <div className="container-financial-board">
        <FinancialCards financialData={financialData} />
        <div className="row">
          <div className="col-6">
            <Card>
              <CardBody className="card-account-actives">
                <Panel
                  className="pb-0"
                  title={t(
                    'dashboard.financialBoard.charts.receivablesByAccount'
                  )}
                >
                  <Card className="pt-0">
                    <CardBody className="pt-0">
                      <ReceivablesByAccountChart
                        dataLabels={accountsLabels}
                        numbersChart={accountsValues}
                        options={options}
                      />
                    </CardBody>
                  </Card>
                  <Scrollbar className="scroll-legends" alwaysShowTracks>
                    <div className="chart-legends">
                      {accountsLabels
                        ? accountsLabels.map((item, index) => (
                            <div key={uuid()}>
                              <span
                                style={{
                                  backgroundColor: colors[index],
                                }}
                              />
                              {item}
                            </div>
                          ))
                        : null}
                    </div>
                  </Scrollbar>
                </Panel>
              </CardBody>
            </Card>
          </div>
          <div className="col-6">
            <Card>
              <CardBody className="card-account-actives">
                <Panel
                  title={t(
                    'dashboard.financialBoard.charts.receivablesByCategory'
                  )}
                  className="card card-account-actives card-body"
                >
                  <Card className="pt-0">
                    <CardBody className="pt-0">
                      <ReceivablesByCategoryChart
                        dataLabels={categorysLabels}
                        numbersChart={categorysValues}
                        options={options}
                      />
                    </CardBody>
                  </Card>
                  <Scrollbar className="scroll-legends" alwaysShowTracks>
                    <div className="chart-legends">
                      {categorysLabels
                        ? categorysLabels.map((item, index) => (
                            <div key={uuid()}>
                              <span
                                style={{
                                  backgroundColor: colors[index],
                                }}
                              />
                              {item}
                            </div>
                          ))
                        : null}
                    </div>
                  </Scrollbar>
                </Panel>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

FinancialBoard.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation('common')(FinancialBoard);
